import {
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { Switch } from 'utoniq-ui'

import {
  ArtistNotificationChannelsType,
  ArtistNotificationSettingsInput,
  ArtistNotificationSettingsType,
  NotificationChannelsType,
  NotificationSettingsType,
  UserNotificationSettingsInput,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  isArtist: boolean
  newPost: boolean
  replyPost: boolean
  newMission: boolean
  replyCommentMission: boolean
  requestTrade: boolean
  commentTrade: boolean
  commentThread: boolean
  replyCommentThread: boolean
  agreedTrade: boolean
  declinedTrade: boolean
  startCampaign: boolean
  endCampaign: boolean
  startLiveStream: boolean
  artistReplyPost: boolean
  artistNewThread: boolean
  artistMembershipSubscribe: boolean
  announcementOfficial: boolean
  gachaUpcomingExpiry: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onToggle: (item: UserNotificationSettingsInput) => Promise<void>
  onArtistToggle: (item: ArtistNotificationSettingsInput) => Promise<void>
}

export type ItemList = {
  label: string
  hidden?: boolean
  items: {
    label: string
    checked: boolean
    hidden?: boolean
    onChange: () => Promise<void>
  }[]
}[]

export const EmailNotificationSettingsTemplate: Template<State, Action> = ({
  state,
  onToggle,
  onArtistToggle,
}) => {
  const { t } = useTranslation('emailNotificationSettings')
  const { routerNames, routerPages } = useRouter()

  const list = useMemo<ItemList>(
    () => [
      {
        label: t('postLabel'),
        hidden: false,
        items: [
          {
            label: t('newPost'),
            checked: state.newPost,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.PostNewPost,
                flag: !state.newPost,
              })
            },
          },
          {
            label: t('replyComment'),
            checked: state.replyPost,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.PostReplyPost,
                flag: !state.replyPost,
              })
            },
          },
          {
            label: t('artistReplyPost'),
            hidden: !state.isArtist,
            checked: state.artistReplyPost,
            onChange() {
              return onArtistToggle({
                channel: ArtistNotificationChannelsType.Email,
                type: ArtistNotificationSettingsType.PostReplyPost,
                flag: !state.artistReplyPost,
              })
            },
          },
        ],
      },
      {
        label: t('missionLabel'),
        hidden: false,
        items: [
          {
            label: t('replyCommentMission'),
            checked: state.replyCommentMission,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.MissionReplyCommentMission,
                flag: !state.replyCommentMission,
              })
            },
          },
        ],
      },
      {
        label: t('campaignLabel'),
        hidden: !config.modules.campaign,
        items: [
          {
            label: t('newCampaign'),
            checked: state.startCampaign,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.CampaignStartCampaign,
                flag: !state.startCampaign,
              })
            },
          },
          {
            label: t('endCampaign'),
            checked: state.endCampaign,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.CampaignEndCampaign,
                flag: !state.endCampaign,
              })
            },
          },
        ],
      },
      /**
       * @description
       * Open when function is implemented in the future
       */
      // {
      //   label: t('campaignLabel'),
      //   hidden: !config.modules.campaign,
      //   items: [
      //     {
      //       label: t('newCampaign'),
      //       checked: state.newCampaign,
      //       onChange() {},
      //     },
      //   ],
      // },
      {
        label: t('threadLabel'),
        hidden: !config.modules.thread,
        items: [
          {
            label: t('commentCreationThread'),
            checked: state.commentThread,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.ThreadChannelCommentThread,
                flag: !state.commentThread,
              })
            },
          },
          {
            label: t('replyCommentThread'),
            checked: state.replyCommentThread,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.ThreadChannelReplyCommentThread,
                flag: !state.replyCommentThread,
              })
            },
          },
          {
            label: t('newThread'),
            hidden: !state.isArtist,
            checked: state.artistNewThread,
            onChange() {
              return onArtistToggle({
                channel: ArtistNotificationChannelsType.Email,
                type: ArtistNotificationSettingsType.ArtistThreadChannelNewThread,
                flag: !state.artistNewThread,
              })
            },
          },
        ],
      },
      {
        label: t('gachaLabel'),
        hidden: !config.modules.gacha,
        items: [
          {
            label: t('announce'),
            checked: state.gachaUpcomingExpiry,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.GachaUpcomingExpiry,
                flag: !state.gachaUpcomingExpiry,
              })
            },
          },
        ],
      },
      {
        label: t('tradeLabel'),
        hidden: !config.modules.trade,
        items: [
          {
            label: t('receiveTrade'),
            checked: state.requestTrade,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.TradeRequestTrade,
                flag: !state.requestTrade,
              })
            },
          },
          {
            label: t('commentTrade'),
            checked: state.commentTrade,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.TradeCommentTrade,
                flag: !state.commentTrade,
              })
            },
          },
          /**
           * @description
           * Open when function is implemented in the future
           */
          // {
          //   label: t('resultTrade'),
          //   checked: state.resultTrade,
          //   onChange() {},
          // },
          /**
           * @deprecated it'll remove in the future
           */
          {
            label: t('agreedTrade'),
            checked: state.agreedTrade,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.TradeAgreedTrade,
                flag: !state.agreedTrade,
              })
            },
          },
          /**
           * @deprecated it'll remove in the future
           */
          {
            label: t('declinedTrade'),
            checked: state.declinedTrade,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.TradeDeclinedTrade,
                flag: !state.declinedTrade,
              })
            },
          },
        ],
      },
      {
        label: t('membershipLabel'),
        hidden: !state.isArtist,
        items: [
          {
            label: t('membership'),
            checked: state.artistMembershipSubscribe,
            onChange() {
              return onArtistToggle({
                channel: ArtistNotificationChannelsType.Email,
                type: ArtistNotificationSettingsType.MembershipSubscribe,
                flag: !state.artistMembershipSubscribe,
              })
            },
          },
        ],
      },
      {
        label: t('announce'),
        items: [
          {
            label: t('announcementOfficial'),
            checked: state.announcementOfficial,
            onChange() {
              return onToggle({
                channel: NotificationChannelsType.Email,
                type: NotificationSettingsType.AnnouncementOfficial,
                flag: !state.announcementOfficial,
              })
            },
          },
        ],
      },
      /**
       * @description
       * Open when function is implemented in the future
       */
      // {
      //   label: t('newsLabel'),
      //   items: [
      //     {
      //       label: t('noticeFromManagement'),
      //       checked: state.noticeFromManagement,
      //       onChange() {},
      //     },
      //   ],
      // },
    ],
    [
      onArtistToggle,
      onToggle,
      state.agreedTrade,
      state.artistMembershipSubscribe,
      state.artistNewThread,
      state.artistReplyPost,
      state.commentTrade,
      state.declinedTrade,
      state.endCampaign,
      state.isArtist,
      state.newMission,
      state.newPost,
      state.replyPost,
      state.requestTrade,
      state.startCampaign,
      state.startLiveStream,
      state.announcementOfficial,
      state.replyCommentMission,
      state.commentThread,
      state.replyCommentThread,
      t,
    ]
  )

  return (
    <BaseLayout
      header={{
        title: t('title'),
        back: true,

        defaultBackPage: routerPages.Setting,
      }}
    >
      <Head title={routerNames.EmailNotificationSettings} />
      {list.map(
        ({ label, hidden, items }, index) =>
          !hidden && (
            <List
              key={index}
              sx={{ bgcolor: 'grey.50' }}
              subheader={
                <ListSubheader sx={{ pb: 2, pt: 4, px: 5 }}>
                  <Typography variant="subtitle2" color="text.hint">
                    {label}
                  </Typography>
                </ListSubheader>
              }
            >
              {items.map(
                (item, itemIndex) =>
                  !item.hidden && (
                    <ListItem
                      divider
                      key={`${index}-${itemIndex}`}
                      secondaryAction={
                        <Switch
                          checked={item.checked}
                          onChange={item.onChange}
                        />
                      }
                    >
                      <ListItemText
                        primary={item.label}
                        slotProps={{
                          primary: { variant: 'body1' },
                        }}
                      />
                    </ListItem>
                  )
              )}
            </List>
          )
      )}
    </BaseLayout>
  )
}
